import { AccountCircle, Logout } from '@mui/icons-material';
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React from 'react';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';

import { HOME_PATH, isTransactionFabVisible } from '../../constants/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { NotificationBell } from '../Notifications';
import { ThemeToggle } from '../Theme';

import AddTransactionFab from './AddTransactionFab';
import MobileBottomNavigation from './MobileBottomNavigation';
import MobileNavigation from './MobileNavigation';
import Navigation from './Navigation';


const MAIN_CONTENT_ID = 'main-content';

const Layout = () => {
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  // 모바일 드로어 상태: 햄버거 버튼(MobileNavigation)과 하단탭 "더보기"가 공유
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { pathname } = useLocation();
  // 모바일 "거래 기록" FAB 가 떠 있는 화면은 마지막 내용이 FAB 에 가리지 않게 하단 여백을 더 둔다
  const showTransactionFab = isMobile && isTransactionFabVisible(pathname);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    handleClose();
  };

  // "본문으로 건너뛰기": 해시 이동 대신 본문 요소에 직접 포커스 (basename/해시 라우팅과 무관하게 동작)
  const handleSkipToMain = (event) => {
    event.preventDefault();
    const main = document.getElementById(MAIN_CONTENT_ID);
    if (main) {
      main.focus();
      main.scrollIntoView?.({ block: 'start' });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box
        component="a"
        href={`#${MAIN_CONTENT_ID}`}
        onClick={handleSkipToMain}
        sx={{
          position: 'absolute',
          left: 8,
          top: 8,
          zIndex: (t) => t.zIndex.tooltip + 1,
          px: 2,
          py: 1,
          borderRadius: 1,
          fontWeight: 600,
          textDecoration: 'none',
          bgcolor: 'background.paper',
          color: 'text.primary',
          border: '2px solid',
          borderColor: 'primary.main',
          transform: 'translateY(-200%)',
          '&:focus': { transform: 'translateY(0)' },
        }}
      >
        본문으로 건너뛰기
      </Box>
      <AppBar position="static" elevation={1}>
        <Toolbar>
          {/* 모바일: 햄버거 메뉴, 데스크톱: 탭 네비게이션 (조건부 렌더링) */}
          {isMobile ? (
            <MobileNavigation open={drawerOpen} onOpenChange={setDrawerOpen} />
          ) : null}

          {/* 로고 = 홈 링크 */}
          <Typography
            variant="h6"
            component={RouterLink}
            to={HOME_PATH}
            aria-label="StackStock 홈"
            sx={{ flexShrink: 0, color: 'inherit', textDecoration: 'none' }}
          >
            StackStock
          </Typography>

          {/* 모바일 알림 벨 */}
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
              <NotificationBell />
            </Box>
          )}

          {/* 데스크톱 네비게이션 */}
          {!isMobile && (
            <Box sx={{ flexGrow: 1, minWidth: 0, mx: 1, display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
              <Navigation />
            </Box>
          )}

          {/* 테마 토글 및 사용자 정보 - 데스크톱에서만 표시 */}
          {!isMobile && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { md: 1, lg: 2 },
              flexShrink: 0,
            }}>
            <ThemeToggle />
            <NotificationBell />
            <Chip
              avatar={<Avatar sx={{ bgcolor: 'primary.main' }}>
                {(user?.nickname || user?.username)?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </Avatar>}
              label={user?.nickname || user?.username || user?.email || 'User'}
              variant="outlined"
              sx={{
                color: 'inherit',
                borderColor: 'divider',
                '& .MuiChip-avatar': {
                  color: 'primary.contrastText',
                },
              }}
            />
            <IconButton
              size="large"
              aria-label="계정 메뉴"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleLogout}>
                <Logout sx={{ mr: 1 }} />
                로그아웃
              </MenuItem>
            </Menu>
          </Box>
          )}
        </Toolbar>
      </AppBar>
      
      <Container
        component="main"
        id={MAIN_CONTENT_ID}
        tabIndex={-1}
        maxWidth="xl"
        sx={{
          outline: 'none',
          flex: 1, 
          py: { xs: 2, sm: 3, md: 3 },
          pb: { xs: showTransactionFab ? 17 : 10, md: 3 }, // 모바일(md 미만)에서 하단 네비게이션(+거래 기록 FAB) 공간 확보
          px: { xs: 2, sm: 3, md: 4 }, // 태블릿에서 적절한 여백
        }}
      >
        <Outlet />
      </Container>
      
      {/* 모바일 하단 네비게이션 (조건부 렌더링) */}
      {isMobile && <MobileBottomNavigation onMoreClick={() => setDrawerOpen(true)} />}

      {/* 모바일 전역 "거래 기록" FAB (노출 경로는 constants/navigation) */}
      {isMobile && <AddTransactionFab />}
    </Box>
  );
};

export default Layout;